import {
  getTrialDaysLeft,
  getStatusStyles,
  getStatusText,
} from '@features/applications/components/HireflixPlanPurchaseModal/helpers';
import HireflixPlanPurchaseModal from '@features/applications/components/HireflixPlanPurchaseModal/HireflixPlanPurchaseModal';
import { useHireflixPlanAutoRefill } from '@features/applications/hooks/useHireflixPlanAutoRefill';
import {
  Alert,
  Box,
  Button,
  Chip,
  Stack,
  Switch,
  Typography,
  Divider,
  CircularProgress,
  FormControlLabel,
} from '@mui/material';
import { authContext } from '@staffedupusa/shared-components';
import { hireflixCta, hireflixCtaHover, white } from '@styles/colors';
import { useContext, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

const ManageInterviewBillingTab = () => {
  const [errorText, setErrorText] = useState('');
  const [hireflixPlanModal, setHireflixPlanModal] = useState(false);
  const [autoRefill, setAutoRefill] = useState(false);

  const {
    authData: { user },
  } = useContext(authContext) as any;

  const plan =
    user?.enterprise?.hire_flix_plan || user?.company?.hire_flix_plan;

  useEffect(() => {
    if (plan?.auto_refill !== undefined) {
      setAutoRefill(plan.auto_refill);
    }
  }, [plan]);

  const { hireflixPlanAutoRefill, isHireflixPlanAutoRefillLoading } =
    useHireflixPlanAutoRefill({ setErrorText });

  const handlePlanAutoRefill = async (value: boolean) => {
    try {
      setErrorText('');
      hireflixPlanAutoRefill(
        { auto_refill: value },
        {
          onSuccess: (data) => {
            toast.success(
              data?.message || 'HireFlix Auto Refill updated successfully',
            );
          },
        },
      );
    } catch (err) {
      console.error('Auto refill failed:', err);
    }
  };

  const trialDaysLeft = useMemo(
    () => getTrialDaysLeft(plan?.trial_start_date),
    [plan?.trial_start_date],
  );

  const statusStyles = useMemo(
    () => getStatusStyles(plan?.status),
    [plan?.status],
  );

  const statusText = useMemo(() => getStatusText(plan?.status), [plan?.status]);

  const openModal = () => setHireflixPlanModal(true);
  const closeModal = () => setHireflixPlanModal(false);

  return (
    <Box
      width="100%"
      mt={2}
      borderRadius={8}
      padding={4}
      sx={{
        boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
        bgcolor: white,
        position: 'relative',
      }}
    >
      {errorText && (
        <Alert
          severity="error"
          sx={{
            width: '100%',
            borderRadius: '8px',
            backgroundColor: 'rgb(251, 224, 224)',
            color: 'rgb(214, 71, 71)',
          }}
        >
          {errorText}
        </Alert>
      )}

      <Stack direction="row" width={1} spacing={2} alignItems="center">
        <Typography variant="h3" fontSize="24px" fontWeight={700}>
          Interview Billing Status:
        </Typography>

        <Chip
          label={statusText}
          sx={{
            backgroundColor: statusStyles.bg,
            color: statusStyles.color,
          }}
          variant="filled"
          size="small"
        />
      </Stack>

      <Divider sx={{ width: '100%', mt: 3, mb: 5 }} />

      {!plan && (
        <Stack spacing={2} width="100%" mt={2}>
          <Typography variant="body1" color="text.secondary">
            Your <strong>30-day</strong> trial activates after the first video
            interview is submitted — and you’ll have{' '}
            <strong>unlimited submissions</strong> throughout the entire period.
          </Typography>
        </Stack>
      )}

      {plan?.status === 'Trial' && (
        <Stack spacing={2} width="100%" mt={2}>
          <Typography variant="body1" color="text.secondary">
            <strong>Unlimited recorded interviews</strong> — {trialDaysLeft}{' '}
            days left
          </Typography>

          <Button
            size="medium"
            variant="contained"
            onClick={openModal}
            sx={{
              mt: '20px !important',
              backgroundColor: hireflixCta,
              '&:hover': { backgroundColor: hireflixCtaHover },
            }}
          >
            Upgrade Plan
          </Button>
        </Stack>
      )}

      {plan?.status === 'Paid' && (
        <Box width="100%">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Box>
              <Typography variant="h5" color="text.primary">
                <strong>Interview Credit Balance:</strong>
              </Typography>
              <Typography variant="body2" mt={1}>
                Credits Never Expire - Credits are only deducted for completed
                video interviews
              </Typography>
            </Box>

            <Typography variant="h3" fontWeight={700}>
              {plan?.credit_limit}
              <Typography variant="body2">Credits</Typography>
            </Typography>
          </Stack>

          <Box
            sx={{
              mt: 4,
              mb: 5,
              borderRadius: 10,
              px: 3,
              py: 3,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              border: '1px solid rgb(230, 230, 230)',
              boxShadow: 'rgba(0,0,0,0.05) 0px 1px 2px 0px',
            }}
          >
            <Box>
              <Typography fontWeight={500}>
                Auto Refill Current Package Selection
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Never miss an interview — Automatically refill credits when
                balance reaches zero
              </Typography>
            </Box>

            <FormControlLabel
              disabled={isHireflixPlanAutoRefillLoading}
              sx={{ m: 0 }}
              control={
                <Switch
                  checked={autoRefill}
                  onChange={({ target }) => {
                    setAutoRefill(target.checked);
                    handlePlanAutoRefill(target.checked);
                  }}
                />
              }
              label=""
            />
          </Box>

          <Button
            size="medium"
            variant="contained"
            onClick={openModal}
            fullWidth
            sx={{
              backgroundColor: hireflixCta,
              '&:hover': { backgroundColor: hireflixCtaHover },
            }}
          >
            Upgrade Plan
          </Button>
        </Box>
      )}

      {isHireflixPlanAutoRefillLoading && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 2,
          }}
        >
          <CircularProgress sx={{ color: hireflixCta }} />
        </Box>
      )}

      {hireflixPlanModal && (
        <HireflixPlanPurchaseModal
          isOpen={hireflixPlanModal}
          handleClose={closeModal}
        />
      )}
    </Box>
  );
};

export default ManageInterviewBillingTab;
